import React, { useState } from 'react';
import { Lock, AlertCircle } from 'lucide-react';
import { TextInput } from './TextInput';
import { authService } from '../lib/auth';

interface SessionRenewalModalProps {
  isOpen: boolean;
  onRenewed: () => void;
  onSignOut: () => void;
}

export const SessionRenewalModal: React.FC<SessionRenewalModalProps> = ({
  isOpen,
  onRenewed,
  onSignOut,
}) => {
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [isLoading, setIsLoading] = useState(false);

  const user = authService.getCurrentUser();

  if (!isOpen) return null;

  const handleRenew = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!user || !password) {
      setError('Informe sua senha para continuar');
      return;
    }
    setIsLoading(true);
    setError('');
    try {
      await authService.login(user.email, password);
      setPassword('');
      onRenewed();
    } catch {
      setError('Senha incorreta. Tente novamente.');
    } finally {
      setIsLoading(false);
    }
  };

  const handleSignOut = () => {
    authService.logout();
    onSignOut();
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-60 flex items-center justify-center z-[9999]">
      <div className="bg-white rounded-lg shadow-xl w-full max-w-sm mx-4">
        <div className="p-6 border-b border-gray-200">
          <h2 className="text-lg font-semibold text-gray-900">Sessão expirada</h2>
          <p className="text-sm text-gray-500 mt-1">
            Sua sessão expirou. Confirme sua senha para continuar.
          </p>
          {user && (
            <p className="text-sm font-medium text-gray-700 mt-2">{user.email}</p>
          )}
        </div>

        <form onSubmit={handleRenew} className="p-6 space-y-4">
          <TextInput
            label="Senha"
            type="password"
            icon={<Lock className="w-5 h-5" />}
            value={password}
            onChange={(e) => { setPassword(e.target.value); setError(''); }}
            error={error}
            autoFocus
          />

          {error && !password && (
            <p className="text-sm text-red-600 flex items-center gap-1">
              <AlertCircle className="w-4 h-4 flex-shrink-0" />
              {error}
            </p>
          )}

          <div className="flex gap-3 pt-2">
            <button
              type="button"
              onClick={handleSignOut}
              className="flex-1 px-4 py-2 text-gray-700 bg-gray-100 hover:bg-gray-200 rounded-lg transition-colors text-sm"
              disabled={isLoading}
            >
              Sair
            </button>
            <button
              type="submit"
              className="flex-1 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors text-sm"
              disabled={isLoading || !password}
            >
              {isLoading ? 'Renovando...' : 'Renovar Sessão'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};
